<script setup>

import {onMounted, ref, watch} from "vue";

const props = defineProps({
    task: {
        type: Object,
        required: true,
    }
})

const isExpanded = ref(false)
const taskHasAvailableAt = ref(false)
const taskHasDueAt = ref(false)
const editingTaskName = ref(false)

watch(() => props.task.available_at_day_offset, (newValue) => {
    props.task.due_at_day_offset = newValue + props.task.duration;
})

onMounted(() => {
    taskHasAvailableAt.value = props.task.available_at_day_offset
    taskHasDueAt.value = props.task.due_at_day_offset
})

</script>
<template>
    <div>
        <div class="flex items-center justify-between py-2">
            <div
                v-if="editingTaskName"
                class="flex space-x-2 items-center"
            >
                <input-text
                    v-model:content="task.label"
                    class="!mb-0"
                    autofocus
                />
                <button-success
                    size="sm"
                    @click="editingTaskName = false"
                >OK</button-success>
            </div>
            <div
                v-if="! editingTaskName"
                class="font-bold"
                @click="editingTaskName = true"
            >
                {{ task.label }}
                <i class="fa-solid fa-pen-to-square text-gray-400 ml-2 cursor-pointer"></i>
            </div>
            <div class="flex items-center space-x-4">
                <div class="text-gray-400 text-xs">
                    {{ task.type === 'routine' ? 'Routine' : 'Todo' }}
                </div>
                <button-transparent
                    size="sm"
                    @click="isExpanded = ! isExpanded"
                    class="transition-all"
                    :class="isExpanded ? 'rotate-180' : ''"
                >
                    <icon-chevron class="w-4 h-4" />
                </button-transparent>
            </div>
        </div>
        <div
            v-if="isExpanded"
            class="bg-gradient-to-b from-white to-gray-50 -mx-4 md:-mx-6 px-4 py-2 md:px-6 -mb-4"
        >
            <div v-if="task.type === 'todo'">
                <div class="flex flex-col md:flex-row justify-between md:items-center md:space-x-8 min-h-12">
                    <div class="md:-mb-3 md:w-36 text-xs">
                        <toggle-switch
                            :disabled="task.type === 'routine' && taskHasAvailableAt"
                            label="Has start date"
                            v-model:content="taskHasAvailableAt"
                        ></toggle-switch>
                    </div>
                    <div
                        v-if="taskHasAvailableAt"
                        class="flex space-x-4 items-center text-xs"
                    >
                        <span>Task starts</span>
                        <input-number
                            :show-step-ui="true"
                            :step="1"
                            min="1"
                            max="1000"
                            v-model:content="task.available_at_day_offset"
                            class="!mb-0 w-24"
                        />
                        <span class="md:whitespace-nowrap">days after list is created</span>
                    </div>
                </div>
                <div class="flex flex-col md:flex-row my-8 md:mt-0 md:mb-4 justify-between md:items-center md:space-x-8 min-h-12">
                    <div class="md:-mb-3 md:w-36 text-xs">
                        <toggle-switch
                            label="Has due date"
                            v-model:content="taskHasDueAt"
                        ></toggle-switch>
                    </div>
                    <div
                        v-if="taskHasDueAt"
                        class="flex space-x-4 items-center text-xs"
                    >
                        <span>Task is due at {{ task.due_at_time }}</span>
                        <input-number
                            :show-step-ui="true"
                            :step="1"
                            :min="task.available_at_day_offset"
                            max="1000"
                            v-model:content="task.due_at_day_offset"
                            class="!mb-0 w-24"
                        />
                        <span class="md:whitespace-nowrap">days after list is created</span>
                    </div>
                </div>
            </div>
            <div
                v-if="task.type === 'routine'"
            >
                <div v-if="task.repeat_rule_type === 'weekdays'">
                    <div class="text-xs mb-2">Select weekdays</div>
                    <div class="grid grid-cols-7 gap-2 mb-4">
                        <button
                            v-for="(day, index) in ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']"
                            :key="index"
                            type="button"
                            @click="{
                                if (task.repeat_rule.weekdays.includes(index + 1)) {
                                    task.repeat_rule.weekdays = task.repeat_rule.weekdays.filter(d => d !== (index + 1));
                                } else {
                                    task.repeat_rule.weekdays.push(index + 1);
                                }
                            }"
                            :class="[
                                'px-2 py-2 text-xs font-medium rounded-md transition-colors',
                                task.repeat_rule.weekdays.includes(index+1)
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            ]"
                        >
                            {{ day }}
                        </button>
                    </div>
                </div>

                <div class="md:grid grid-cols-2 items-center gap-4">
                    <div>
                        <label
                            class="text-xs"
                        >
                            Due time
                        </label>
                        <input-time
                            :content="task.due_at_time"
                            @update:content="task.due_at_time = $event"
                        />
                    </div>

                    <div class="md:-mb-3 text-xs">
                        <toggle-switch
                            label="Notify when the task is due"
                            v-model:content="task.notify_at_due_at_time"
                        ></toggle-switch>
                    </div>
                </div>
            </div>
            <div>
                <div class="text-xs mb-2">
                    <div>Priority</div>
                    <ul class="flex space-x-2">
                        <li>
                            <button
                                :class="task.priority === 1 ? 'border-black' : ''"
                                size="sm"
                                class="p-2 rounded-md bg-red-500 border-2 text-white w-8 h-8 flex justify-center items-center"
                                @click="task.priority = 1"
                            >
                                <span>1</span>
                            </button>
                        </li>
                        <li>
                            <button
                                :class="task.priority === 2 ? 'border-black' : ''"
                                size="sm"
                                class="p-2 rounded-md bg-yellow-500 border-2 text-white w-8 h-8 flex justify-center items-center"
                                @click="task.priority = 2"
                            >
                                <span>2</span>
                            </button>
                        </li>
                        <li>
                            <button
                                :class="task.priority === 3 ? 'border-black' : ''"
                                size="sm"
                                class="p-2 rounded-md bg-blue-500 border-2 text-white w-8 h-8 flex justify-center items-center"
                                @click="task.priority = 3"
                            >
                                <span>3</span>
                            </button>
                        </li>
                        <li>
                            <button
                                :class="task.priority === 0 ? 'border-black' : ''"
                                size="sm"
                                class="p-2 rounded-md bg-gray-200 border-2 text-gray-700 w-8 h-8 flex justify-center items-center"
                                @click="task.priority = 0"
                            >
                                <span>-</span>
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
            <div
                v-if="task.type === 'routine'"
                class="mt-4 mb-2"
            >
                <h3 class="text-sm font-bold">Routine settings</h3>
                <div
                    v-if="task.repeat_rule_type === 'days'"
                    class="flex space-x-4 items-center text-xs"
                >
                    <span>Task repeats every</span>
                    <input-number
                        :show-step-ui="true"
                        :step="1"
                        min="1"
                        max="1000"
                        v-model:content="task.repeat_rule.days"
                        class="!mb-0 w-24"
                    />
                    <span class="md:whitespace-nowrap">days after start date</span>
                </div>
            </div>
        </div>
    </div>
</template>
